import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { camerasApi, pushPreferencesApi } from '@/api/endpoints'
import { queryKeys, queryOptions } from '@/config/queryConfig'
import { pushCategoriesOf } from '@/lib/pushBell'
import { myCameraFiltersOf, myCameraModesOf } from '@/lib/myPush'
import { useAuthStore } from '@/store/authStore'
import type { Camera, Event, PushCategory, PushFilters } from '@/types'

/**
 * #139 — the camera's own visual whitelist (`push_class_names`, owner-set,
 * applies to everyone the camera is shared with) and this account's category
 * filter (`filters`) both have to allow an event before a push fires. Either
 * one alone can silently zero out a camera for this account — a camera set to
 * "person only" plus an account that only wants "animal" is silence forever,
 * and nothing else on screen says so. This panel is purely informational: it
 * changes no behaviour, it just names the cameras caught in that intersection
 * so the silence isn't also invisible.
 */

/** class name -> every category the push catalog puts it in (usually one). */
function buildClassToCategories(
  groups: { category: PushCategory; class_names: string[] }[],
): Record<string, PushCategory[]> {
  const map: Record<string, PushCategory[]> = {}
  for (const group of groups) {
    for (const cls of group.class_names) {
      const cats = map[cls] ?? (map[cls] = [])
      cats.push(group.category)
    }
  }
  return map
}

/**
 * #139 — the push catalog deliberately excludes the reserved coarse/fallback
 * classes (`person`/`vehicle`/`animal` — `RESERVED_PUSH_CLASS_IDS` in
 * serializers.py), but the per-camera picker (`/cameras/{id}/available-classes`)
 * does not, so `camera.push_class_names` can legitimately contain one of them.
 * Looking those up in `classToCategories` returns nothing, and treating "not in
 * the catalog" as "unreachable" is exactly the false warning this fixes.
 * Fall back to the same local taxonomy the in-app bell uses (`pushCategoriesOf`)
 * instead of re-encoding person/vehicle/animal a third time here. That function
 * never returns an empty array for a real class name; the empty-array case is a
 * defensive "can't prove it's dead, so don't say it is" for anything that still
 * fails to map.
 */
const PUSH_CATEGORY_ORDER: PushCategory[] = ['animal', 'human', 'vehicle', 'audio', 'other']


/** CP 391 — a stored camera row as a full filter: a category it does not name is on (push_matches), and its
 *  `_mode` marker is dropped. */
function asFilters(stored: Partial<PushFilters>): PushFilters {
  const full = {} as PushFilters
  for (const cat of PUSH_CATEGORY_ORDER) {
    const f = stored[cat]
    full[cat] = f && typeof f === 'object' ? { enabled: f.enabled !== false, classes: Array.isArray(f.classes) ? f.classes : [] } : { enabled: true, classes: [] }
  }
  return full
}

function categoriesForClass(
  cls: string,
  classToCategories: Record<string, PushCategory[]>,
): PushCategory[] {
  const fromCatalog = classToCategories[cls]
  if (fromCatalog && fromCatalog.length > 0) return fromCatalog
  return pushCategoriesOf({ detected_objects: [cls] } as unknown as Event)
}

/**
 * `null` when the camera can still reach this account under the current
 * filter; otherwise which reason applies to it.
 */
function deadZoneReason(
  camera: Camera,
  filters: PushFilters,
  classToCategories: Record<string, PushCategory[]>,
  currentUserId: number | null,
): 'deadZonePushOff' | 'deadZoneCamera' | null {
  if (camera.push_enabled === false) {
    // #140 — a camera you switched off yourself isn't a silent dead zone: the
    // toggle lives in the same settings area and it was your own call. Only
    // a camera SHARED to you and muted by its owner is genuinely invisible —
    // you can't see their per-camera toggle, so this is the only thing that
    // would explain the silence. Unknown current user (null) is conservative:
    // never accuse a camera's owner when we can't tell who's viewing.
    // #361 — camera.user is the owner's id (a number), not a User object.
    if (currentUserId !== null && camera.user !== currentUserId) {
      return 'deadZonePushOff'
    }
    return null
  }

  // CP 159 — a camera that has switched off every category the account still wants is
  // silent no matter what its class lists say. This is the dead zone the per-camera
  // category filter introduces, and nothing else surfaces it.
  const cameraFilters = camera.push_filters
  if (cameraFilters && Object.keys(cameraFilters).length > 0) {
    const anyCategoryLive = PUSH_CATEGORY_ORDER.some((cat) => {
      const cam = cameraFilters[cat] ?? { enabled: true, classes: [] }
      return cam.enabled && filters[cat].enabled
    })
    if (!anyCategoryLive) return 'deadZoneCamera'
  }

  // #139 — an audio-bearing event used to bypass the camera's VISUAL whitelist entirely,
  // so a camera could always reach a user who wanted audio.
  // CP 159 makes that conditional: the camera filter now covers audio as well, so this
  // only holds while the camera's own audio category is still enabled — which is what
  // every legacy class list translates to (class_names_to_filters leaves audio
  // enabled and unrestricted), so nothing changes for a camera nobody has configured.
  const cameraAllowsAudio = cameraFilters?.audio ? cameraFilters.audio.enabled : true
  if (filters.audio.enabled && cameraAllowsAudio) return null

  if (camera.push_class_names.length === 0) {
    return null
  }

  const cameraCategories = new Set<PushCategory>()
  for (const cls of camera.push_class_names) {
    const cats = categoriesForClass(cls, classToCategories)
    if (cats.length === 0) return null // unmapped class — can't prove unreachable, stay silent
    for (const cat of cats) cameraCategories.add(cat)
  }

  const canReach = Array.from(cameraCategories).some((cat) => {
    const catFilter = filters[cat]
    if (!catFilter.enabled) return false
    if (catFilter.classes.length === 0) return true
    return catFilter.classes.some((cls) => camera.push_class_names.includes(cls))
  })

  return canReach ? null : 'deadZoneCamera'
}

export function PushDeadZones({ filters }: { filters: PushFilters }) {
  const { t } = useTranslation('settings')
  const currentUser = useAuthStore((s) => s.user)

  const { data: cameras } = useQuery({
    queryKey: queryKeys.cameras.all(),
    queryFn: () => camerasApi.getAll(),
    ...queryOptions.cameras.all,
  })

  // Same key PushCategoriesSection uses for the catalog — the two mount
  // together, so this dedupes against that fetch instead of doubling it.
  const { data: catalog } = useQuery({
    queryKey: ['push-preferences-catalog'],
    queryFn: pushPreferencesApi.catalog,
  })

  // CP 391 — the person's own camera rows ("My phone alerts"), the bell's key: a camera with a row is judged by
  // that row, not by the account filter edited here (the handler uses the row instead of the account row). Waited
  // for, so no false warning shows while it loads; if it cannot be read the account filter judges every camera.
  const { data: rows, isPending: rowsPending } = useQuery({
    queryKey: queryKeys.pushPreferences.cameras(),
    queryFn: () => pushPreferencesApi.getCameras(),
  })

  if (!cameras || !catalog || rowsPending) return null

  const classToCategories = buildClassToCategories(catalog.groups)
  const ownRows = myCameraFiltersOf(rows) ?? {}
  const ownModes = myCameraModesOf(rows)

  const dead = cameras
    .map((camera) => ({
      camera,
      // "Only graffiti" is the person's own call to get no normal alerts: not a silent dead zone (as #140 treats a
      // camera you switched off yourself). #406 — "Suspicious + graffiti" the same.
      reason: ownModes[camera.id] === 'critical_only' || ownModes[camera.id] === 'suspicious'
        ? null
        : deadZoneReason(
            camera,
            ownRows[camera.id] ? asFilters(ownRows[camera.id]) : filters,
            classToCategories,
            currentUser?.id ?? null,
          ),
    }))
    .filter((entry): entry is { camera: Camera; reason: NonNullable<typeof entry.reason> } => entry.reason !== null)

  if (dead.length === 0) return null

  return (
    <div
      role="alert"
      className="rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400"
    >
      <p className="font-medium">{t('pushCategories.deadZoneTitle')}</p>
      <ul className="mt-1 list-disc space-y-0.5 pl-4">
        {dead.map(({ camera, reason }) => (
          <li key={camera.id}>
            {reason === 'deadZonePushOff'
              ? t('pushCategories.deadZonePushOff', { camera: camera.name })
              : t('pushCategories.deadZoneCamera', {
                  camera: camera.name,
                  classes: camera.push_class_names.join(', '),
                })}
          </li>
        ))}
      </ul>
    </div>
  )
}
